import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { AppState, DashboardInstallationColumn, KanbanBoardSelectors, TutorialType } from '@store';
import { ViError } from '@vi-iot/client/models';
import { TutorialsData } from './tutorials.model';
import { TutorialsState } from './tutorials.state';

export namespace TutorialsSelectors {
  export const tutorials = (state: AppState): TutorialsState => state.tutorials;

  export function getState(): MemoizedSelector<
    AppState,
    TutorialsState,
    DefaultProjectorFn<TutorialsState>
  > {
    return createSelector(tutorials, (state: TutorialsState) => state);
  }

  export const getLoading = createSelector(
    tutorials,
    (state: TutorialsState): boolean => state.loading
  );

  export const getLoaded = createSelector(
    tutorials,
    (state: TutorialsState): boolean => state.loaded
  );

  export const getSaving = createSelector(
    tutorials,
    (state: TutorialsState): boolean => state.saving
  );

  export const getSaved = createSelector(
    tutorials,
    (state: TutorialsState): boolean => state.saved
  );

  export const getError = createSelector(
    tutorials,
    (state: TutorialsState): ViError => state.error
  );

  export const getTutorials = createSelector(
    tutorials,
    (state: TutorialsState): TutorialsData[] => state.tutorials
  );

  export function hasTutorialBeenPresented(
    tutorialType: TutorialType
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      tutorials,
      (state: TutorialsState) =>
        !!state.tutorials?.find((tutorial: TutorialsData) => tutorial.tutorialType === tutorialType)
          ?.presented
    );
  }

  export const selectDisplayKanbanInstallationsListTutorial = createSelector(
    tutorials,
    TutorialsSelectors.hasTutorialBeenPresented('kanbanInstallationList' as TutorialType),
    KanbanBoardSelectors.selectInstallationListState,
    (_state: TutorialsState, tutorialPresented: boolean, installationListState): boolean => {
      return (
        !tutorialPresented && installationListState.value !== null && !installationListState.error
      );
    }
  );

  export const selectDisplayKanbanBoardTutorial = createSelector(
    tutorials,
    TutorialsSelectors.hasTutorialBeenPresented('kanbanBoard' as TutorialType),
    KanbanBoardSelectors.selectColumns,
    (
      _state: TutorialsState,
      tutorialPresented: boolean,
      columns: DashboardInstallationColumn[]
    ): boolean => {
      if (!columns) {
        return false;
      }

      return (
        !tutorialPresented &&
        columns.length > 0 &&
        columns.some((column) => column.installations.length > 0)
      );
    }
  );

  export const selectDisplayLandingPageTutorial = createSelector(
    tutorials,
    TutorialsSelectors.hasTutorialBeenPresented('landingPage' as TutorialType),
    (_state: TutorialsState, tutorialPresented: boolean): boolean => {
      return !tutorialPresented;
    }
  );
}
